import { cn } from '@/lib/utils'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { useTranslation } from 'react-i18next'

import { Badge } from '@/components/ui/badge'

interface FileRow {
    id: string
    refNumber: string
    name: string
    client: {
        name: string
        initials: string
        color: string
    }
    status: string | null
    lastUpdate: React.ReactNode
}

interface RecentFilesTableProps {
    files: FileRow[]
    title?: string
    onViewAll?: () => void
    className?: string
}

export function RecentFilesTable({ files, title, onViewAll, className }: RecentFilesTableProps) {
    const { t } = useTranslation()

    const getStatusBadge = (status: string | null) => {
        const statusColors: Record<string, "default" | "secondary" | "destructive" | "outline"> = {
            active: "default",
            audience: "secondary",
            delibere: "secondary",
            suspended: "outline",
            closed: "default",
            archived: "outline",
        };

        const variant = status && statusColors[status] ? statusColors[status] : "secondary";

        if (!status) return <span className="text-muted-foreground">-</span>;

        return (
            <Badge variant={variant}>
                {t(`case_status.${status}`)}
            </Badge>
        );
    }

    return (
        <div className={cn(
            "bg-card rounded-xl shadow-sm border border-border overflow-hidden",
            className
        )}>
            {/* Header */}
            {(title || onViewAll) && (
                <div className="p-5 border-b border-border flex items-center justify-between">
                    {title && (
                        <h3 className="font-bold text-foreground flex items-center gap-2">
                            <svg className="w-5 h-5 text-secondary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                            </svg>
                            {title}
                        </h3>
                    )}
                    {onViewAll && (
                        <button
                            onClick={onViewAll}
                            className="text-xs text-primary dark:text-blue-300 hover:underline"
                        >
                            {t('view_all')}
                        </button>
                    )}
                </div>
            )}

            {/* Table */}
            <div className="overflow-x-auto">
                <table className="w-full text-sm text-right">
                    <thead className="bg-muted/50 text-muted-foreground font-medium border-b border-border">
                        <tr>
                            <th className="px-6 py-3 text-start">{t('file_number')}</th>
                            <th className="px-6 py-3 text-start">{t('file_name')}</th>
                            <th className="px-6 py-3 text-start">{t('client_header')}</th>
                            <th className="px-6 py-3 text-start">{t('status_header')}</th>
                            <th className="px-6 py-3 text-start">{t('last_update_header')}</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                        {files.map((file) => {
                            return (
                                <tr
                                    key={file.id}
                                    className="hover:bg-muted/30 transition-colors cursor-pointer"
                                >
                                    <td className="px-6 py-4 font-mono text-xs text-muted-foreground text-start">
                                        {file.refNumber}
                                    </td>
                                    <td className="px-6 py-4 font-semibold text-foreground text-start">
                                        {file.name}
                                    </td>
                                    <td className="px-6 py-4 text-start">
                                        <div className="flex items-center gap-2">
                                            <Avatar className="w-6 h-6">
                                                <AvatarFallback
                                                    className={cn("text-xs", file.client.color)}
                                                >
                                                    {file.client.initials}
                                                </AvatarFallback>
                                            </Avatar>
                                            <span className="text-foreground/80">
                                                {file.client.name}
                                            </span>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 text-start">
                                        {getStatusBadge(file.status)}
                                    </td>
                                    <td className="px-6 py-4 text-muted-foreground text-start">
                                        {file.lastUpdate}
                                    </td>
                                </tr>
                            )
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    )
}
